// React
import React, { useState, useEffect } from "react";
import s from "./Remitos.module.css";
// Redux
import { useDispatch, useSelector } from "react-redux";
// Assets
import deliveryServices from "../../../api/delivery/DeliveryService";
import Pager from "../../Shared/Pagination/Pagination";
import FormRemitos from "./Create/FormRemitos";
import SearchModal from "./SearchModal";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { dateFormats } from "../../../helpers/convertDate";
import { DetailsRemitos } from "./Details/detailsRemitos";
import { useTranslation } from "react-i18next";
// Material
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
// Utils
import useTranslationException from "../../../helpers/exceptions";

// ========================== | REMITOS | ======================================

export const Remitos = () => {
  // Section: Utils Hooks
  const showExpetion = useTranslationException();
  const dates = dateFormats();
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();

  // Section: Redux Storage Data
  const { textColor } = useSelector((state) => state.local);
  const currentUser = useSelector((state) => state.gamma.logged_user);

  // Section: States
  const [items, setItems] = useState([]);
  const [actualPage, setActualPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCustom, setPageCustom] = useState(1);
  const [sort, setSort] = useState({
    criteria: "DESC",
    field: "date",
  });
  const [loading, setLoading] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const [searching, setSearching] = useState(false);
  const [filters, setFilters] = useState({
    number: 0,
    type: "any",
    dateRemit: null,
    state: "any",
    agency: "all",
    provider: "all",
    observations: "",
  });
  const [newFilters, setNewFilters] = useState({
    number: 0,
    type: "any",
    dateRemit: null,
    state: "any",
    agency: "all",
    provider: "all",
    observations: "",
  });
  const [edit, setEdit] = useState({
    status: false,
    item: {},
  });
  const [openModal, setOpenModal] = useState(false);

  // Section: Functions
  const getData = () => {
    setLoading(true);
    if (searching) {
      deliveryServices
        .searchDeliveryNotes(actualPage, pageSize, newFilters, sort)
        .then((response) => {
          setItems(response.content);
          setTotalPages(response.totalPages);
        })
        .then(() => setLoading(false))
        .catch((e) => {
          showExpetion.showErrorModalForServiceException(
            e,
            { icon: "warning" },
            t("anErrorOccurred,TryAgain"),
            t("sorryErrorOcurred"),
          );
          setLoading(false);
        });
    } else {
      deliveryServices
        .getDeliveryNote(actualPage, pageSize, dispatch, sort)
        .then((response) => {
          setItems(response.content);
          setTotalPages(response.totalPages);
        })
        .then(() => setLoading(false))
        .catch((e) => {
          showExpetion.showErrorModalForServiceException(
            e,
            { icon: "warning" },
            t("anErrorOccurred,TryAgain"),
            t("sorryErrorOcurred"),
          );
          setLoading(false);
        });
    }
  };

  // Section: Effects
  useEffect(() => {
    getData();
  }, [actualPage, pageSize, searching, sort]);

  // Effect: Obtener remito por ID si viene en el state de la navegación
  useEffect(() => {
    const deliveryNoteId = location.state?.deliveryNoteId;
    const approvalDate = location.state?.approvalDate; // Fecha de aprobación del pedido
    if (deliveryNoteId && location.pathname.includes('/detail')) {
      setLoading(true);
      deliveryServices
        .getDeliveryNoteById(deliveryNoteId)
        .then((response) => {
          console.log("📋 Remito obtenido por ID:", JSON.stringify(response, null, 2));
          
          // Workaround: Si el remito no tiene fecha, establecerla con la fecha de aprobación del pedido o la fecha actual
          // Esto es necesario porque el backend a veces no establece el campo date al crear el remito automáticamente
          if (!response.date) {
            // Priorizar la fecha de aprobación del pedido, si está disponible
            response.date = approvalDate || new Date().toISOString();
            console.log("⚠️ Remito sin fecha, establecida con:", response.date);
          }
          
          setItemSelected(response);
        })
        .catch((error) => {
          console.error("Error al obtener remito por ID:", error);
          showExpetion.showErrorModalForServiceException(
            error,
            { icon: "warning" },
            t("anErrorOccurred,TryAgain"),
            t("sorryErrorOcurred"),
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [location.state, location.pathname]);

  // Sections: Functions
  const newDeliveryNote = () => {
    const path = "./create";
    navigate(path);
  };

  // Section: Handlers
  const handleEdit = (remito) => {
    setEdit({
      status: true,
      item: remito,
    });
    newDeliveryNote();
  };

  const handleCancel = (item) => {
    let details = item?.detailList?.map((detail) => {
      return {
        amount: detail.amount,
        gameId: detail.gameId,
        id: detail.id,
        nroCouponFrom: detail.nroCouponFrom,
        nroCouponTo: detail.nroCouponTo,
        raffleId: detail.raffleReadDTO.id,
      };
    });
    let payload = {
      ...item,
      detailList: details,
      state: "Anulado",
      storePassingType: item.storePassingType,
      organizationName: currentUser?.memberships[0]?.organization?.name,
      lotteryName: currentUser?.memberships[0]?.organization?.name,
    };
    Swal.fire({
      title: t("deliveryNoteCancelTitle"),
      text: t("deliveryNoteCancelText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "primary.main",
      cancelButtonColor: "#d33",
      confirmButtonText: t("confirm"),
      cancelButtonText: t("cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        deliveryServices
          .editDeliveryNote(payload)
          .then(() => {
            Swal.fire(
              t("deliveryNoteCancelSuccessTitle"),
              t("deliveryNoteCancelSuccessText"),
              "success",
            );
          })
          .then(() => {
            getData();
          })
          .catch((e) => {
            showExpetion.showErrorModalForServiceException(
              e,
              t("tryInAMoment"),
              t("errorInCancel"),
            );
            navigate("../remitos");
          });
      }
    });
  };

  const handleDetail = (item) => {
      console.log("Remito enviado al botón de imprimir:", item.id);
    let path = `./impreso/${item.id}`;
    navigate(path);
  };

  const openDetail = (item) => {
    setItemSelected(item);
    let path = `./detail`;
    navigate(path);
  };

  const handleSort = (item) => {
    setSort((prev) => ({
      criteria:
        prev.field === item
          ? prev.criteria === "DESC"
            ? "ASC"
            : "DESC"
          : "DESC",
      field: item,
    }));
  };

  const cellStyles = {
    fontFamily: "Roboto, sans-serif",
    fontSize: ".85rem",
    fontWeight: "400",
    borderBottom: "1px solid rgba(0, 0, 0, 0.455)",
    cursor: "pointer",
  };

  // Section: JSX
  return (
    <Grid container className={s.container}>
      {location.pathname.includes("create") ? (
        <FormRemitos
          sort={sort}
          getData={getData}
          edit={edit}
          setEdit={setEdit}
          actualPage={actualPage}
          pageSize={pageSize}
          setItems={setItems}
        />
      ) : location.pathname.includes("detail") ? (
        <DetailsRemitos
          form={itemSelected}
          setItemSelected={setItemSelected}
          setItems={setItems}
        />
      ) : (
        <>
          <Grid item container xs={11} className={s.header}>
            <Grid item lg={4} md={6} xs={12} className={s.titleContainer}>
              <InventoryRoundedIcon
                sx={{ fontSize: "1.8rem", color: "primary.main" }}
              />
              <h4>{t("deliveryNotes")}</h4>
              <Button
                variant="contained"
                className={s.add_btn}
                color="primary"
                onClick={newDeliveryNote}
              >
                {t("add")}
              </Button>
            </Grid>
            <Grid item xs={12} md={4} lg={4} className={s.filters}>
              <Button
                variant="contained"
                className={s.filterButton}
                onClick={() => setOpenModal(!openModal)}
              >
                <TuneRoundedIcon
                  sx={{ color: "secondary.light", fontSize: "1.7rem" }}
                />
                <p>{t("filter")}</p>
              </Button>

              {searching ? (
                <Button
                  variant="contained"
                  className={s.filterButton}
                  onClick={() => {
                    setActualPage(1);
                    setFilters({
                      number: 0,
                      type: "any",
                      dateRemit: null,
                      state: "any",
                      agency: "all",
                      provider: "all",
                      observations: "",
                    });
                    setNewFilters({
                      number: 0,
                      type: "any",
                      dateRemit: null,
                      state: "any",
                      agency: "all",
                      provider: "all",
                      observations: "",
                    });
                    setSearching(false);
                  }}
                >
                  <p>{t("clearFilters")}</p>
                </Button>
              ) : null}
            </Grid>
          </Grid>

          {!items?.length && searching ? (
            <Grid item xs={11} className={s.noResults}>
              <p>{t("noResults")}</p>
            </Grid>
          ) : (
            <Grid item container xs={11} className={s.dataContainer}>
              {!loading ? (
                items?.length ? (
                  <TableContainer className={s.tableContainer}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              cursor: "pointer",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                            onClick={() => handleSort("type")}
                          >
                            {`${t("type")} ${sort.field === "type" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                            onClick={() => handleSort("state")}
                          >
                            {t("state")}
                            {`${sort.field === "state" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                            onClick={() => handleSort("number")}
                          >
                            {t("number")}
                            {`${sort.field === "number" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              cursor: "pointer",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                            onClick={() => handleSort("date")}
                          >
                            {`${t("dischargeDate")} ${sort.field === "date" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                            onClick={() => handleSort("storeRemit")}
                          >
                            {`${t("source")} ${sort.field === "storeRemit" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                            onClick={() => handleSort("storeReceptor")}
                          >
                            {`${t("destination")} ${sort.field === "storeReceptor" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                            onClick={() => handleSort("observations")}
                          >
                            {`${t("observations")} ${sort.field === "observations" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                          >
                            {t("edit")}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                          >
                            {t("remitCancel")}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "primary.main",
                              color: textColor,
                              fontSize: ".85rem",
                              fontWeight: 500,
                              fontFamily: "Roboto, sans-Serif",
                            }}
                          >
                            {t("print")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items?.map((remito, i) => {
                          return (
                            <React.Fragment key={i}>
                              <TableRow
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "secondary.light",
                                  },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                  onClick={() => openDetail(remito)}
                                >
                                  {remito.type}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                  onClick={() => openDetail(remito)}
                                >
                                  {remito.state}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                  onClick={() => openDetail(remito)}
                                >
                                  {remito.type === "Salida"
                                    ? remito.nroOutput
                                    : remito.nroInput}
                                </TableCell>
                                <TableCell
                                  width="11%"
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                  onClick={() => openDetail(remito)}
                                >
                                  {remito.date ? remito.date.split("T")[0] : "-"}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                  onClick={() => openDetail(remito)}
                                >
                                  {remito.storeRemit}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                  onClick={() => openDetail(remito)}
                                >
                                  {remito.storeReceptor}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                  onClick={() => openDetail(remito)}
                                >
                                  {remito.observations || "-"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                >
                                  {remito.state === "En Proceso" ? (
                                    <EditRoundedIcon
                                      className={s.iconbtn}
                                      sx={{ color: "primary.dark" }}
                                      onClick={() => handleEdit(remito)}
                                    />
                                  ) : (
                                    <EditRoundedIcon
                                      sx={{ color: "grey", fontSize: "1.6rem" }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                >
                                  {remito.state === "En Proceso" ? (
                                    <DoDisturbIcon
                                      sx={{ color: "primary.main" }}
                                      className={s.iconbtn}
                                      onClick={() => handleCancel(remito)}
                                    />
                                  ) : (
                                    <DoDisturbIcon
                                      sx={{ color: "grey", fontSize: "1.6rem" }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    ...cellStyles,
                                    backgroundColor:
                                      i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                  }}
                                >
                                  {(remito.state !== "Anulado") ? (
                                    <LocalPrintshopIcon
                                      className={s.iconbtn}
                                      sx={{ color: "primary.dark" }}
                                      onClick={() => handleDetail(remito)}
                                    />
                                  ) : (
                                    <LocalPrintshopIcon
                                      sx={{ color: "grey", fontSize: "1.6rem" }}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <span style={{ fontSize: "1.5rem" }}>{t("noResults")}</span>
                  </Grid>
                )
              ) : (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <CircularProgress size={"3rem"} />
                </Grid>
              )}
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: "50px",
            }}
          >
            <Pager
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={actualPage}
              setPage={setActualPage}
              totalPages={totalPages}
              pageCustom={pageCustom}
              setPageCustom={setPageCustom}
            />
          </Grid>
          <SearchModal
            setNewFilters={setNewFilters}
            open={openModal}
            closeModal={setOpenModal}
            setFilters={setFilters}
            filters={filters}
            setSearching={setSearching}
            setActualPage={setActualPage}
          />
        </>
      )}
    </Grid>
  );
};
